"use client"

import React, { useMemo } from "react"
import { View, StyleSheet } from "react-native"
import { Table } from "../../DataTable/Table"
import { ColumnDefinition } from "../../../types/table"
import { processNodeData, findNodesByName, getNodesByParent, getAllNodesOfType } from "../../../utils/assetNodeUtils"
import type { TabComponentProps } from "../../../types/tabs"

export const StringingTab: React.FC<TabComponentProps> = ({ selectedNode, projectData, menuAction }) => {
    // Define columns for Stringing Progress Report
    const columns = useMemo<ColumnDefinition[]>(() => {
        return [
            {
                key: "Span Number",
                title: "Span Number",
                sortable: true,
                filterable: true,
                align: "center" as const,
                type: "string",
            },
            {
                key: "Start Location",
                title: "Start Location",
                sortable: true,
                filterable: true,
                align: "center" as const,
                type: "string",
            },
            {
                key: "End Location",
                title: "End Location",
                sortable: true,
                filterable: true,
                align: "center" as const,
                type: "string",
            },
            {
                key: "Completion Status",
                title: "Completion Status",
                sortable: true,
                filterable: true,
                align: "center" as const,
                type: "string",
            },
            {
                key: "Completion Status Date",
                title: "Completion Status Date",
                sortable: true,
                filterable: true,
                align: "center" as const,
                type: "string",
            },
            {
                key: "Progress",
                title: "Progress",
                sortable: true,
                filterable: true,
                align: "left" as const,
                type: "string",
            },
            {
                key: "Comments",
                title: "Comments",
                sortable: true,
                filterable: true,
                align: "left" as const,
                type: "string",
            },
        ]
    }, [])

    // Generate span data based on selection (same logic as ErectionTab but for spans)
    const spanData = useMemo(() => {
        if (!projectData || !columns.length) return []

        // Handle span selection
        if (selectedNode?.type === "span") {
            const spanData = findNodesByName(projectData, selectedNode.name, "span")
            const exactSpanData = spanData.find(
                (span) => span.name === selectedNode.name && span.type === "span"
            )
            return exactSpanData ? [processNodeData(exactSpanData, columns)] : []
        }

        // Handle section selection
        if (selectedNode?.type === "section") {
            const sectionSpans = getNodesByParent(projectData, selectedNode.name, "section", "span")
            return sectionSpans.map((span) => processNodeData(span, columns))
        }

        // Handle no selection or line selection - show all spans
        if (!selectedNode || selectedNode.type === "line") {
            const allSpans = getAllNodesOfType(projectData, "span")

            // Create a Map to store unique spans by name
            const uniqueSpansMap = new Map()
            allSpans.forEach((span) => {
                if (!uniqueSpansMap.has(span.name)) {
                    uniqueSpansMap.set(span.name, span)
                }
            })

            const uniqueSpans = Array.from(uniqueSpansMap.values())
            return uniqueSpans.map((span) => processNodeData(span, columns))
        }

        return []
    }, [selectedNode, projectData, columns])

    if (!columns.length) return null

    return (
        <View style={styles.container}>
            <Table
                columns={columns}
                data={spanData}
                title="Stringing Progress Report"
                tableName="stringing"
                pageSize={10}
                menuAction={menuAction}
            />
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
})
